import type { Paginated } from "./types";
import { formatDateTime } from "./utils";

export type RecognitionStatus =
  "queued" | "recognizing" | "completed" | "failed";
export type RecognitionFilter = {
  q?: string;
  status?: RecognitionStatus | "active";
  from?: string;
  to?: string;
  userQuery?: string;
  sort?: "latest" | "submissions";
};
export interface RecognitionUser {
  id: string;
  account: string;
  name: string;
  jobs: number;
  active: number;
  completed: number;
  totalJobs: number;
  successfulJobs: number;
  enabled: boolean;
  failed: number;
  latestAt: string;
}
export interface RecognitionUsers extends Paginated<RecognitionUser> {
  summary: {
    total: number;
    users: number;
    active: number;
    completed: number;
    failed: number;
  };
}
export interface RecognitionJob {
  id: string;
  status: RecognitionStatus;
  createdAt: string;
  updatedAt: string;
  error: string | null;
  preview: string;
  imageCount: number;
  planCount: number;
}
export interface RecognizedPlan {
  id: string;
  title: string;
  location?: string;
  description?: string;
  date: string;
  startTime: string;
  endDate: string;
  endTime: string;
  startAt?: string;
  endAt?: string;
  recurrence?: {
    id: string;
    groupId?: string;
    weekdays: number[];
    weekStarts: string[];
    excludedDates?: string[];
  };
}
export interface RecognitionDetail extends Omit<
  RecognitionJob,
  "preview" | "imageCount" | "planCount"
> {
  user: Pick<RecognitionUser, "id" | "account" | "name">;
  input: {
    text: string;
    images: { index: number; url: string }[];
    referenceDate: string | null;
    timeZone: string | null;
  };
  plans: RecognizedPlan[];
}
export const recognitionStatus: Record<
  RecognitionStatus,
  { label: string; tone: "neutral" | "clay" | "sage" | "danger" }
> = {
  queued: { label: "排队中", tone: "clay" },
  recognizing: { label: "识别中", tone: "clay" },
  completed: { label: "已添加", tone: "sage" },
  failed: { label: "未完成", tone: "danger" },
};
export function planTime(plan: RecognizedPlan) {
  return `${formatDateTime(plan.startAt || `${plan.date} ${plan.startTime}`, true)} — ${formatDateTime(plan.endAt || `${plan.endDate} ${plan.endTime}`, true)}`;
}
export function groupRecognizedPlans(plans: RecognizedPlan[]) {
  const groups = new Map<string, RecognizedPlan[]>();
  for (const plan of plans) {
    const key = plan.recurrence?.groupId || plan.recurrence?.id || plan.id;
    const group = groups.get(key) || [];
    group.push(plan);
    groups.set(key, group);
  }
  return [...groups.entries()].map(([id, items]) => ({
    id,
    items,
    plan: items[0],
  }));
}
export function recognitionText(plans: RecognizedPlan[]) {
  return groupRecognizedPlans(plans)
    .map(({ plan, items }, index) =>
      [
        `${index + 1}. ${plan.title}`,
        ...items.map(planTime),
        plan.location ? `地点：${plan.location}` : "",
        plan.description || "",
      ]
        .filter(Boolean)
        .join("\n"),
    )
    .join("\n\n");
}
export function recognitionDateRange(from: string, through: string) {
  const start = from ? new Date(`${from}T00:00:00`) : null;
  const end = through ? new Date(`${through}T00:00:00`) : null;
  if (end) end.setDate(end.getDate() + 1);
  if (
    (start && !Number.isFinite(+start)) ||
    (end && !Number.isFinite(+end)) ||
    (start && end && +start >= +end)
  )
    throw new Error("请选择有效的起止日期");
  return { from: start?.toISOString(), to: end?.toISOString() };
}
